import { api } from "./api";
import type {
  CostProjectionDataType,
  CostProjectionValidation,
  PaginatedResponse,
} from "types/DataTypes";

type GetCostProjectionsArgs = {
  type?: string;
  searchTerm?: string;
};

type GetCostProjectionByIdArgs = {
  costProjectionId: string | number;
};

type GetCostProjectionsByFileIdArgs = {
  fileId: string | number;
};

type PatchCostProjectionArgs = {
  costProjectionId: string | number;
  data: Partial<CostProjectionDataType>;
};

type GetCostProjectionAuditHistoryArgs = {
  id: string | number;
};

export const costProjectionAPI = api.injectEndpoints({
  endpoints: (build) => ({
    getCostProjections: build.query<
      PaginatedResponse<CostProjectionDataType>,
      GetCostProjectionsArgs
    >({
      query: ({ type, searchTerm }) => {
        const params = new URLSearchParams();
        if (type) params.append("type", type);
        return {
          url: `api/v1/cost-projections/?${params.toString()}`,
          method: "GET",
          params: searchTerm ? { filter: searchTerm } : undefined,
        };
      },
      providesTags: ["CostProjectionTable"],
    }),
    getCostProjectionById: build.query<
      CostProjectionDataType,
      GetCostProjectionByIdArgs
    >({
      query: ({ costProjectionId }) => ({
        url: `api/v1/cost-projections/${costProjectionId}/`,
        method: "GET",
      }),
      providesTags: ["MCP", "LCP"],
    }),
    getCostProjectionsByFileId: build.query<
      PaginatedResponse<CostProjectionDataType>,
      GetCostProjectionsByFileIdArgs
    >({
      query: ({ fileId }) => ({
        url: `api/v1/cost-projections/?file__id=${fileId}&include_validations=true`,
        method: "GET",
      }),
      providesTags: (_result, _error, { fileId }) => [
        { type: "CostProjectionTable", id: `FILE-${fileId}` },
      ],
    }),
    getCostProjectionValidationById: build.query<
      CostProjectionValidation,
      GetCostProjectionByIdArgs
    >({
      query: ({ costProjectionId }) => ({
        url: `api/v1/cost-projections/${costProjectionId}/validate/`,
        method: "GET",
      }),
      providesTags: ["CostProjectionValidation"],
    }),
    patchCostProjectionById: build.mutation<
      CostProjectionDataType,
      PatchCostProjectionArgs
    >({
      query: ({ costProjectionId, data }) => ({
        url: `api/v1/cost-projections/${costProjectionId}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (
        { costProjectionId, data },
        { dispatch, queryFulfilled },
      ) => {
        const patchResult = dispatch(
          costProjectionAPI.util.updateQueryData(
            "getCostProjectionById",
            { costProjectionId: String(costProjectionId) },
            (draft) => {
              Object.assign(draft, data);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ["MCP", "LCP", "CostProjectionValidation", "RatedAge"],
    }),
    getCostProjectionAuditHistory: build.query<
      unknown,
      GetCostProjectionAuditHistoryArgs
    >({
      query: ({ id }) => ({
        url: `api/v1/cost-projections/${id}/audit_trail/`,
        method: "GET",
      }),
      providesTags: ["MCP", "LCP"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCostProjectionsQuery,
  useGetCostProjectionByIdQuery,
  usePatchCostProjectionByIdMutation,
  useGetCostProjectionValidationByIdQuery,
  useGetCostProjectionsByFileIdQuery,
  useGetCostProjectionAuditHistoryQuery,
} = costProjectionAPI;
